import times from "lodash/times";

import { useTranslator } from "@eo-locale/react";

import { useRouter } from "next/router";

import Cookies from "js-cookie";

import { useEffect, useState } from "react";

import {
  Box,
  Button,
  FiAlertTriangle,
  useDisclosure,
  SimpleGrid,
  FiEdit2,
  FiTrash,
  HTMLWrapper,
  FiEdit
} from "@/storybook-components";
import ContributionCard from "../common/contribution-card";

import ActionList from "../common/action-list";
import { useAppSelector, useAppDispatch } from "@/redux/store/hooks";
import {
  reportIdea,
  refreshOneIdea,
  removeIdea,
  fetchIdeas,
  fetchIdea
} from "@/redux/features/brainstormers/thunk";

import useUser from "@/hooks/useUser";
import useUnverifiedModal from "@/hooks/useUnverfiedModal";
import {
  setTriggerPoint,
  setModalStatus
} from "@/redux/features/unverified-modal/slice";

import OpenPostDrawer from "../actions/open-post-drawer";

const ListView = ({
  ideaList,
  lastItemRef,
  isLoading,
  contentToLoad,
  fetchSelectedIdea,
  manageVotes,
  allowUnverifiedParticipation,
  canContribute,
  projectVisibility,
  isVotingAllowed,
  brainStormerState,
  projectState
}): JSX.Element => {
  const { translate } = useTranslator();
  const dispatch = useAppDispatch();
  const router = useRouter();
  const editPostDisclosure = useDisclosure();
  const createDisclosure = useDisclosure();

  const { isUnverfiedUserLoggedIn, isRegisteredUserLoggedIn } = useUser();
  const { unverifiedUser, registeredUser } = useAppSelector(
    (state) => state.user
  );
  const {
    status: brainstormerStatus,
    meta,
    drawerStatus,
    idea
  } = useAppSelector((state) => state?.brainstormers);
  const { count: ideasCount } = meta;

  const [isEditTriggered, setIsEditTriggered] = useState(
    brainstormerStatus === "editTriggered"
  );

  const [currentPostData, setCurrentPostData] = useState(null);
  useEffect(() => {
    if (
      brainstormerStatus === "ideaUpdated" &&
      (currentPostData?.id || idea?.id)
    ) {
      dispatch(refreshOneIdea({ ideaId: currentPostData?.id || idea?.id }));
    }
    if (brainstormerStatus === "editTriggered") {
      setIsEditTriggered(true);
    }
  }, [brainstormerStatus]);

  useEffect(() => {
    if (drawerStatus === "update" && isEditTriggered) {
      setTimeout(() => {
        editPostDisclosure.onOpen();
      }, 500);
    }
  }, [drawerStatus, isEditTriggered]);

  const { visibilityMode, id: projectId } = useAppSelector(
    (state) => state.project.project
  );
  const { id: brainstormerId, allowCommentsForIdeas } = useAppSelector(
    (state) => state.brainstormers.brainstormer
  );
  const { moderated } = useAppSelector((state) => state?.site?.site);

  const { triggerPoint } = useAppSelector((state) => state.unverifiedModal);
  const cookieValues = Cookies.get("trigger_point");
  let cookieTriggerPoint;
  if (cookieValues) {
    cookieTriggerPoint = JSON.parse(cookieValues);
  }

  const actionButtonVisibility =
    projectState === "archived" ||
    brainStormerState === "archived" ||
    isVotingAllowed ||
    !(isUnverfiedUserLoggedIn || isRegisteredUserLoggedIn);

  const isActionButtonVisible =
    (visibilityMode === "Panelled" || visibilityMode === "Protected") &&
    !canContribute;

  const registeredUserLogin =
    isRegisteredUserLoggedIn && registeredUser?.profile.login;
  const unverifiedUserLogin = isRegisteredUserLoggedIn && unverifiedUser?.login;
  const userLogin = registeredUserLogin || unverifiedUserLogin;

  const handleReportClick = (currentData) => {
    const { id } = currentData;
    dispatch(
      reportIdea({
        ideaId: id
      })
    );
  };

  const handleEditClick = (currentData) => {
    dispatch(
      fetchIdea({
        projectId,
        brainstormerId,
        ideaId: currentData.id
      })
    ).then((data) => {
      setCurrentPostData(Object.assign(data?.payload, {}));
      setIsEditTriggered(true);
    });
    editPostDisclosure.onOpen();
  };

  const handleDeleteClick = (currentData) => {
    const { id } = currentData;
    dispatch(
      removeIdea({
        projectId,
        brainstormerId,
        ideaId: id
      })
    );
  };

  useEffect(() => {
    if (brainstormerStatus === "ideaDeleted") {
      dispatch(
        fetchIdeas({
          projectId,
          brainstormerId,
          pageSize: 20,
          page: 1
        })
      );
    }
  }, [brainstormerStatus]);

  const menuList = [
    {
      icon: <FiAlertTriangle />,
      title: translate("brainstormers.reportToModerator"),
      type: "report",
      isItemVisible: moderated,
      actionHandler: null,
      hasAlert: true,
      loggedInUser: userLogin,
      menuItemColor: "",
      menuListLabel: translate("brainstormers.reportToModerator"),
      alertContent: {
        header: translate("brainstormers.youWantToReportContent"),
        body: translate("brainstormers.reportContentDesc"),
        buttonPrimary: translate("common.yes"),
        buttonSecondary: translate("common.no"),
        buttonPrimaryVariant: "primary",
        isPrimaryDestructive: false,
        buttonSecondaryVariant: "secondary",
        isSecondaryDestructive: false,
        onPrimaryClickHandler: handleReportClick
      }
    },
    {
      icon: <FiEdit2 />,
      title: translate("common.edit"),
      type: "edit",
      isItemVisible: !actionButtonVisibility && !isActionButtonVisible,
      actionHandler: handleEditClick,
      hasAlert: false,
      loggedInUser: userLogin,
      menuItemColor: "",
      menuListLabel: translate("common.edit"),
      alertContent: null,
      isDisabled: isActionButtonVisible
    },
    {
      icon: <FiTrash />,
      title: translate("common.delete"),
      type: "delete",
      isItemVisible: !actionButtonVisibility && !isActionButtonVisible,
      hasAlert: true,
      loggedInUser: userLogin,
      menuItemColor: "red.600",
      menuListLabel: translate("common.delete"),
      alertContent: {
        header: translate("brainstormers.ideaDeleteContentTitle"),
        body: translate("brainstormers.ideaDeleteContentDescription"),
        buttonPrimary: translate("common.delete"),
        buttonSecondary: translate("common.cancel"),
        buttonPrimaryVariant: "primary",
        isPrimaryDestructive: true,
        buttonSecondaryVariant: "secondary",
        isSecondaryDestructive: false,
        onPrimaryClickHandler: handleDeleteClick
      }
    }
  ];

  const isButtonDisabled =
    ((projectVisibility === "Panelled" || projectVisibility === "Protected") &&
      !canContribute) ||
    isVotingAllowed;

  useUnverifiedModal(allowUnverifiedParticipation, (isProceedAllowed) => {
    if (isProceedAllowed && isEditTriggered) {
      dispatch(
        setTriggerPoint({ ...triggerPoint, isTriggeredPointActive: false })
      );
      if (
        triggerPoint.triggeredSource === "new_idea" ||
        cookieTriggerPoint?.triggeredSource === "new_idea"
      ) {
        createDisclosure.onOpen();
        addQueryParam();
      }
      Cookies.remove("trigger_point");
    }
  });
  const haveYourSayClickHandler = () => {
    if (isButtonDisabled) return;
    // If user is not registered, redirect to login modal
    if (!isUnverfiedUserLoggedIn && !isRegisteredUserLoggedIn) {
      const triggerPoint = {
        triggeredSource: "new_idea",
        isTriggeredPointActive: true
      };
      Cookies.set("trigger_point", JSON.stringify(triggerPoint), {
        expires: 1,
        secure: true,
        sameSite: "Strict"
      });
      dispatch(setModalStatus("create"));
      dispatch(setTriggerPoint(triggerPoint));
    } else {
      createDisclosure.onOpen();
      addQueryParam();
    }
  };

  const addQueryParam = () => {
    router.query.action = "new_idea";
    router.replace(router, "", { shallow: true });
  };

  return (
    <Box mt={12} className="ehq-ideas-list">
      {ideasCount > 0 && ideaList.length > 0 ? (
        <SimpleGrid
          className="ideas-search-node"
          sx={{ mark: { bg: "yellow.300" } }}
          justifyItems="center"
          templateColumns="repeat(auto-fill,minmax(18rem,1fr))"
          gap={14}
        >
          {ideaList.map((datum, index) => {
            return (
              <Box
                minW="18rem"
                maxW="28rem"
                w="100%"
                key={datum.id}
                ref={ideaList.length === index + 1 ? lastItemRef : null}
              >
                <ContributionCard
                  key={datum.id}
                  cardData={datum}
                  clickHandler={() => fetchSelectedIdea(datum.id)}
                  cardLoaded
                  actionList={
                    <ActionList menuList={menuList} currentData={datum} />
                  }
                  manageVotes={manageVotes}
                  handleComment={() => fetchSelectedIdea(datum.id)}
                  showCommentIcon={allowCommentsForIdeas}
                />
              </Box>
            );
          })}
          {isLoading &&
            times(contentToLoad, (index) => {
              return (
                <Box minW="18rem" maxW="28rem" w="100%" key={index}>
                  <ContributionCard
                    key={index}
                    cardData={null}
                    clickHandler={null}
                    cardLoaded={false}
                    actionList={null}
                    manageVotes={null}
                    handleComment={null}
                  />
                </Box>
              );
            })}
        </SimpleGrid>
      ) : ideasCount !== null && ideaList.length === 0 ? (
        projectState !== "archived" &&
        brainStormerState !== "archived" && (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            pt={20}
            gap={4}
          >
            <HTMLWrapper
              html={translate("brainstormers.noContentText")}
              textAlign="center"
              as="p"
            />
            <Button
              variant="primary"
              leftIcon={<FiEdit />}
              fontWeight="semibold"
              fontSize="md"
              onClick={haveYourSayClickHandler}
              isDisabled={isButtonDisabled}
              size="lg"
              width={{ base: "full", lg: "auto" }}
              px={6}
              py={3}
            >
              {translate("brainstormers.newPost")}
            </Button>
          </Box>
        )
      ) : (
        <SimpleGrid
          justifyItems="center"
          templateColumns="repeat(auto-fill,minmax(18rem,1fr))"
          gap={14}
        >
          {times(20, (index) => {
            return (
              <Box minW="18rem" maxW="28rem" w="100%" key={index}>
                <ContributionCard
                  key={index}
                  cardData={null}
                  clickHandler={null}
                  cardLoaded={false}
                  actionList={null}
                  manageVotes={null}
                  handleComment={null}
                />
              </Box>
            );
          })}
        </SimpleGrid>
      )}
      {(isUnverfiedUserLoggedIn || isRegisteredUserLoggedIn) &&
        isEditTriggered && (
          <OpenPostDrawer
            onOpen={editPostDisclosure.onOpen}
            isOpen={editPostDisclosure.isOpen}
            onClose={editPostDisclosure.onClose}
            type="edit"
            isEditPost={isEditTriggered}
            editHandler={setIsEditTriggered}
            currentPostData={idea || currentPostData}
          />
        )}
    </Box>
  );
};

export default ListView;
